// DropdownMenu (DESIGN §3.7.4): Radix DropdownMenu. Einträge 36 (mit Unterzeile 48), Icon 16 links
// `fg-secondary`, Kürzel rechts als Kbd, Hover/Fokus `tile`; destruktiv `danger-fg` am Ende nach
// Trenner. Checkbox-/Radio-Einträge mit Häkchen rechts, Untermenüs mit Chevron, Gruppenköpfe.
import type { ComponentProps, ReactNode } from 'react';
import { CaretRightIcon, CheckIcon } from '@phosphor-icons/react';
import { DropdownMenu as MenuPrimitive } from 'radix-ui';
import { floatingSurface, menuGroupLabel, menuItem, menuSeparator } from './_lib/menuStyles';
import './_lib/motion.css';
import { useLayerStyle } from './_lib/layer';
import { cn } from '../basis/kern';

export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;
export const DropdownMenuGroup = MenuPrimitive.Group;
export const DropdownMenuRadioGroup = MenuPrimitive.RadioGroup;
export const DropdownMenuSub = MenuPrimitive.Sub;

const contentClass = cn(
  floatingSurface,
  'min-w-52 max-w-[calc(100vw-32px)] overflow-y-auto p-1',
  'max-h-[calc(var(--radix-dropdown-menu-content-available-height)-16px)]',
);

export function DropdownMenuContent({
  sideOffset = 8,
  align = 'end',
  collisionPadding = 16,
  className,
  style,
  ...rest
}: ComponentProps<typeof MenuPrimitive.Content>) {
  const layerStyle = useLayerStyle(style);
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content
        sideOffset={sideOffset}
        align={align}
        collisionPadding={collisionPadding}
        {...rest}
        style={layerStyle}
        className={cn(contentClass, className)}
      />
    </MenuPrimitive.Portal>
  );
}

interface ItemContentProps {
  icon?: ReactNode;
  description?: ReactNode;
  shortcut?: string;
  children: ReactNode;
}

function ItemContent({ icon, description, shortcut, children }: ItemContentProps) {
  return (
    <>
      {icon ? (
        <span
          aria-hidden="true"
          className="inline-flex shrink-0 text-fg-secondary group-data-[destructive=true]/item:text-danger-fg [&>svg]:size-4"
        >
          {icon}
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{children}</span>
        {description ? (
          <span className="truncate text-label text-fg-secondary">{description}</span>
        ) : null}
      </span>
      {shortcut ? (
        <kbd className="ml-4 shrink-0 rounded-xs bg-pill-card px-1.5 font-sans text-chip text-fg-secondary">
          {shortcut}
        </kbd>
      ) : null}
    </>
  );
}

export interface DropdownMenuItemProps
  extends Omit<ComponentProps<typeof MenuPrimitive.Item>, 'children'>, ItemContentProps {
  /** Destruktiv (Löschen, Entziehen): `danger-fg`, gehört ans Ende nach einem Trenner. */
  destructive?: boolean;
}

export function DropdownMenuItem({
  icon,
  description,
  shortcut,
  destructive,
  className,
  children,
  ...rest
}: DropdownMenuItemProps) {
  return (
    <MenuPrimitive.Item
      {...rest}
      data-destructive={destructive || undefined}
      className={cn(
        'group/item',
        menuItem,
        description && 'min-h-12',
        destructive && 'text-danger-fg data-[highlighted]:bg-danger-soft',
        className,
      )}
    >
      <ItemContent icon={icon} description={description} shortcut={shortcut}>
        {children}
      </ItemContent>
    </MenuPrimitive.Item>
  );
}

const indicator = (
  <MenuPrimitive.ItemIndicator className="absolute right-3 inline-flex text-accent-fg">
    <CheckIcon size={16} weight="bold" aria-hidden="true" />
  </MenuPrimitive.ItemIndicator>
);

export function DropdownMenuCheckboxItem({
  icon,
  description,
  className,
  children,
  ...rest
}: Omit<ComponentProps<typeof MenuPrimitive.CheckboxItem>, 'children'> &
  Omit<ItemContentProps, 'shortcut'>) {
  return (
    <MenuPrimitive.CheckboxItem {...rest} className={cn(menuItem, 'pr-9', className)}>
      <ItemContent icon={icon} description={description}>
        {children}
      </ItemContent>
      {indicator}
    </MenuPrimitive.CheckboxItem>
  );
}

export function DropdownMenuRadioItem({
  icon,
  description,
  className,
  children,
  ...rest
}: Omit<ComponentProps<typeof MenuPrimitive.RadioItem>, 'children'> &
  Omit<ItemContentProps, 'shortcut'>) {
  return (
    <MenuPrimitive.RadioItem {...rest} className={cn(menuItem, 'pr-9', className)}>
      <ItemContent icon={icon} description={description}>
        {children}
      </ItemContent>
      {indicator}
    </MenuPrimitive.RadioItem>
  );
}

export function DropdownMenuLabel({
  className,
  ...rest
}: ComponentProps<typeof MenuPrimitive.Label>) {
  return <MenuPrimitive.Label {...rest} className={cn(menuGroupLabel, className)} />;
}

export function DropdownMenuSeparator({
  className,
  ...rest
}: ComponentProps<typeof MenuPrimitive.Separator>) {
  return <MenuPrimitive.Separator {...rest} className={cn(menuSeparator, className)} />;
}

export function DropdownMenuSubTrigger({
  icon,
  className,
  children,
  ...rest
}: Omit<ComponentProps<typeof MenuPrimitive.SubTrigger>, 'children'> & {
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <MenuPrimitive.SubTrigger
      {...rest}
      className={cn(menuItem, 'data-[state=open]:bg-tile', className)}
    >
      <ItemContent icon={icon}>{children}</ItemContent>
      <CaretRightIcon size={14} aria-hidden="true" className="shrink-0 text-fg-secondary" />
    </MenuPrimitive.SubTrigger>
  );
}

export function DropdownMenuSubContent({
  className,
  collisionPadding = 16,
  style,
  ...rest
}: ComponentProps<typeof MenuPrimitive.SubContent>) {
  const layerStyle = useLayerStyle(style);
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.SubContent
        sideOffset={4}
        collisionPadding={collisionPadding}
        {...rest}
        style={layerStyle}
        className={cn(contentClass, className)}
      />
    </MenuPrimitive.Portal>
  );
}
